import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getFoods, createFood, updateFood, deleteFood } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Search, Pencil, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';

const CATEGORIES = [
  'Grains', 'Vegetables', 'Fruits', 'Legumes', 'Dairy', 'Protein', 
  'Nuts', 'Beverages', 'Snacks', 'Desserts', 'Condiments', 'Oils', 'Other'
];

const emptyFood = {
  name: '',
  category: 'Grains',
  emoji: '',
  glycemicIndex: 50,
  glycemicLoad: 10,
  sugarSpikeRating: 5,
  carbsPer100g: 0,
  fibrePer100g: 0,
  proteinPer100g: 0,
  fatPer100g: 0,
  caloriesPer100g: 0,
  servingSizeG: 100,
  isGoodForType1: false,
  isGoodForType2: false,
  diabeticNotes: '',
  barcode: '',
  source: '',
  ingredients: [],
  isActive: true,
};

const FoodsPage = () => {
  const location = useLocation();
  const [foods, setFoods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [type1Filter, setType1Filter] = useState(null);
  const [type2Filter, setType2Filter] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [formData, setFormData] = useState(emptyFood);
  const [ingredientInput, setIngredientInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchFoods = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (category && category !== 'all') params.category = category;
      if (type1Filter !== null) params.type1 = type1Filter;
      if (type2Filter !== null) params.type2 = type2Filter;

      const response = await getFoods(params);
      setFoods(response.data.items);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load foods');
    } finally {
      setIsLoading(false);
    }
  }, [page, search, category, type1Filter, type2Filter]);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  useEffect(() => {
    if (location.state?.openNew) {
      openNewPanel();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const calculateSpikeRating = (gi, carbs) => {
    const gl = (gi * carbs) / 100;
    if (gl <= 5) return 1;
    if (gl <= 10) return 3;
    if (gl <= 15) return 5;
    if (gl <= 20) return 7;
    return Math.min(10, Math.round(gl / 3));
  };

  const handleGIChange = (value) => {
    const gi = parseInt(value) || 0;
    const spike = calculateSpikeRating(gi, formData.carbsPer100g);
    const gl = (gi * formData.carbsPer100g) / 100;
    setFormData({ ...formData, glycemicIndex: gi, sugarSpikeRating: spike, glycemicLoad: parseFloat(gl.toFixed(1)) });
  };

  const handleCarbsChange = (value) => {
    const carbs = parseFloat(value) || 0;
    const spike = calculateSpikeRating(formData.glycemicIndex, carbs);
    const gl = (formData.glycemicIndex * carbs) / 100;
    setFormData({ ...formData, carbsPer100g: carbs, sugarSpikeRating: spike, glycemicLoad: parseFloat(gl.toFixed(1)) });
  };

  const openNewPanel = () => {
    setEditingFood(null);
    setFormData(emptyFood);
    setErrors({});
    setPanelOpen(true);
  };

  const openEditPanel = (food) => {
    setEditingFood(food);
    setFormData({
      ...food,
      ingredients: food.ingredients || [],
    });
    setErrors({});
    setPanelOpen(true);
  };

  const addIngredient = () => {
    if (ingredientInput.trim()) {
      setFormData({
        ...formData,
        ingredients: [...formData.ingredients, ingredientInput.trim()]
      });
      setIngredientInput('');
    }
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.category) newErrors.category = true;
    if (!formData.emoji.trim()) newErrors.emoji = true;
    if (!formData.diabeticNotes.trim()) newErrors.diabeticNotes = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (editingFood) {
        await updateFood(editingFood.id, formData);
        toast.success('Food updated successfully');
      } else {
        await createFood(formData);
        toast.success('Food created successfully');
      }
      setPanelOpen(false);
      fetchFoods();
    } catch (error) {
      toast.error('Error saving food — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingFood) return;
    setIsSaving(true);
    try {
      await deleteFood(editingFood.id);
      toast.success('Food deleted successfully');
      setPanelOpen(false);
      setDeleteDialogOpen(false);
      fetchFoods();
    } catch (error) {
      toast.error('Error deleting food — try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="foods-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Foods</h1>
        <Button 
          onClick={openNewPanel}
          className="bg-[#1A6B3C] hover:bg-[#155a32]"
          data-testid="new-food-btn"
        >
          <Plus size={16} className="mr-1" /> New Food
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center bg-white p-4 rounded-lg border">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            placeholder="Search foods..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-sm">T1</Label>
          <Switch 
            checked={type1Filter === true}
            onCheckedChange={(v) => { setType1Filter(v ? true : null); setPage(1); }}
            data-testid="type1-filter"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">T2</Label>
          <Switch 
            checked={type2Filter === true}
            onCheckedChange={(v) => { setType2Filter(v ? true : null); setPage(1); }}
            data-testid="type2-filter"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3" data-testid="foods-loading">
            {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : foods.length === 0 ? (
          <div className="p-8 text-center" data-testid="foods-empty">
            <p className="text-gray-500 mb-4">No foods found</p>
            <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]">
              <Plus size={16} className="mr-1" /> Add First Food
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">GI</TableHead>
                  <TableHead className="text-center">Spike</TableHead>
                  <TableHead className="text-center">T1</TableHead>
                  <TableHead className="text-center">T2</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foods.map((food) => (
                  <TableRow key={food.id} data-testid={`food-row-${food.id}`}>
                    <TableCell className="text-xl">{food.emoji}</TableCell>
                    <TableCell className="font-medium">{food.name}</TableCell>
                    <TableCell className="text-gray-500">{food.category}</TableCell>
                    <TableCell className="text-center">{food.glycemicIndex}</TableCell>
                    <TableCell className="text-center">{food.sugarSpikeRating}</TableCell>
                    <TableCell className="text-center">{food.isGoodForType1 ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">{food.isGoodForType2 ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${food.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditPanel(food)}
                        data-testid={`edit-food-${food.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-gray-500">
                Showing {foods.length} of {total} foods
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm">Page {page} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="slide-panel-520 overflow-y-auto" aria-describedby="food-panel-desc">
          <SheetHeader>
            <SheetTitle>{editingFood ? 'Edit Food' : 'New Food'}</SheetTitle>
            <SheetDescription>
              {editingFood ? 'Modify food details and nutritional information' : 'Add a new food item to the database'}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className={errors.name ? 'text-red-500' : ''}>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'border-red-500' : ''}
                  data-testid="food-name-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className={errors.category ? 'text-red-500' : ''}>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={errors.emoji ? 'text-red-500' : ''}>Emoji *</Label>
                <Input
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="🥦"
                  className={errors.emoji ? 'border-red-500' : ''}
                  data-testid="food-emoji-input"
                />
              </div>
              <div>
                <Label>GI (0-100) *</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.glycemicIndex}
                  onChange={(e) => handleGIChange(e.target.value)}
                  data-testid="food-gi-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>Carbs/100g *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.carbsPer100g}
                  onChange={(e) => handleCarbsChange(e.target.value)}
                />
              </div>
              <div>
                <Label>Fibre/100g *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fibrePer100g}
                  onChange={(e) => setFormData({ ...formData, fibrePer100g: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Protein/100g *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.proteinPer100g}
                  onChange={(e) => setFormData({ ...formData, proteinPer100g: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Fat/100g *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fatPer100g}
                  onChange={(e) => setFormData({ ...formData, fatPer100g: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Calories/100g *</Label>
                <Input
                  type="number"
                  value={formData.caloriesPer100g}
                  onChange={(e) => setFormData({ ...formData, caloriesPer100g: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Serving (g) *</Label>
                <Input
                  type="number"
                  value={formData.servingSizeG}
                  onChange={(e) => setFormData({ ...formData, servingSizeG: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Spike Preview */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4 text-sm">
                <span>GL: <strong>{formData.glycemicLoad}</strong></span>
                <span>Spike Rating: <strong>{formData.sugarSpikeRating}/10</strong></span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.isGoodForType1}
                  onCheckedChange={(v) => setFormData({ ...formData, isGoodForType1: v })}
                />
                <Label>Good for Type 1</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.isGoodForType2}
                  onCheckedChange={(v) => setFormData({ ...formData, isGoodForType2: v })}
                />
                <Label>Good for Type 2</Label>
              </div>
            </div>

            <div>
              <Label className={errors.diabeticNotes ? 'text-red-500' : ''}>Diabetic Notes *</Label>
              <Textarea
                value={formData.diabeticNotes}
                onChange={(e) => setFormData({ ...formData, diabeticNotes: e.target.value })}
                rows={3}
                className={errors.diabeticNotes ? 'border-red-500' : ''}
                data-testid="food-notes-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Barcode (optional)</Label>
                <Input
                  value={formData.barcode || ''}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                />
              </div>
              <div>
                <Label>Source (optional)</Label>
                <Input
                  value={formData.source || ''}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                />
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <Label>Ingredients</Label>
              <div className="tag-input-container mt-1">
                {formData.ingredients.map((ing, idx) => (
                  <span key={idx} className="tag-chip">
                    {ing}
                    <button onClick={() => removeIngredient(idx)}><X size={12} /></button>
                  </span>
                ))}
                <input
                  type="text"
                  value={ingredientInput}
                  onChange={(e) => setIngredientInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIngredient())}
                  placeholder="Type & press Enter"
                  className="flex-1 min-w-[100px] border-none outline-none text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive}
                onCheckedChange={(v) => setFormData({ ...formData, isActive: v })}
              />
              <Label>Active</Label>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                className="bg-[#1A6B3C] hover:bg-[#155a32]"
                data-testid="save-food-btn"
              >
                {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Save
              </Button>
              {editingFood && (
                <Button 
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isSaving}
                  data-testid="delete-food-btn"
                >
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setPanelOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The food will be deactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FoodsPage;
